import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
} from "@/components/ui";
import { useSupportedTokens } from "@/hooks/api/useFaucet";
import { cn } from "@/lib/utils";
import { SUPPORTED_TOKENS } from "@/services/data/tokens";
import { isEmpty } from "lodash";
import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export type SupportedTokensProps = {
  tokens: string[];
  setTokens: (tokens: string[]) => void;
};

export function SupportedTokens(props: Readonly<SupportedTokensProps>) {
  const { tokens, setTokens } = props;
  const { data: supportedTokens } = useSupportedTokens();

  // For display purpose only, actual update for the query is in debounce
  const [selectedTokens, setSelectedTokens] = useState<string[]>(tokens);
  const [selectedToken, setSelectedToken] = useState<string>("ETH");

  const MULTIPLE_TOKENS = false;

  const allTokens = useMemo(() => {
    return supportedTokens?.tokens.map((token) => {
      return token.symbol;
    });
  }, [supportedTokens]);

  const tokensList = useMemo(() => {
    if (!isEmpty(supportedTokens)) {
      return supportedTokens;
    } else {
      return { tokens: SUPPORTED_TOKENS }; // default
    }
  }, [supportedTokens]);

  // Enable for multiple-request
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handleOnCheckMultipleDisplay(symbol: string) {
    if (selectedTokens.includes(symbol)) {
      const newList = selectedTokens.filter((token) => {
        return token !== symbol;
      });
      setSelectedTokens([...newList]);
    } else {
      selectedTokens.push(symbol);
      setSelectedTokens([...selectedTokens]);
    }
  }

  function getTokenName(symbol: string) {
    return supportedTokens?.tokens.find((token) => {
      return token.symbol === symbol;
    });
  }

  // Display update
  function handleOnCheckDisplay(symbol: string) {
    setSelectedToken(symbol);
  }

  // Actual value update for query
  function handleOnCheckChange() {
    // Enable for multiple-request
    // setTokens([...selectedTokens]);
    setTokens([selectedToken]);
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleOnCheckChange();
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToken]);

  return (
    <DropdownMenu>
      <fieldset className="rounded-md border border-solid border-input-secondary bg-background dark:bg-transparent">
        <legend className="ml-2 px-2 text-sm text-muted-foreground">
          Select Token:
        </legend>
        <DropdownMenuTrigger
          className={cn(
            "group text-nowrap flex gap-2 items-center px-4 pb-3 pt-2 justify-between w-full"
          )}
        >
          {/* Enable for multiple-tokens request */}
          {/* <p className="text-sm font-medium min-w-28">{`${
          tokens.length > 1
            ? `${tokens.length} tokens selected`
            : `${tokens.length} token selected`
        }`}</p> */}
          <Skeleton isLoading={!getTokenName(selectedToken)?.name}>
            <p className="font-medium min-w-10">{`${
              getTokenName(selectedToken)?.name
            } (${selectedToken})`}</p>
          </Skeleton>
          <ChevronDown className="group-data-[state=open]:rotate-180 transition-all" />
        </DropdownMenuTrigger>
      </fieldset>

      <DropdownMenuContent className="w-[var(--radix-popper-anchor-width)]">
        {MULTIPLE_TOKENS && (
          <>
            <div className="flex gap-2 justify-between items-center">
              <DropdownMenuLabel asChild>
                <Button
                  variant="ghost"
                  onClick={() => {
                    const allSupportedTokens = allTokens ?? ["ETH"];
                    setTokens(allSupportedTokens);
                    setSelectedTokens(allSupportedTokens);
                  }}
                >
                  Select All
                </Button>
              </DropdownMenuLabel>
              <DropdownMenuLabel asChild>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setTokens(["ETH"]);
                    setSelectedTokens(["ETH"]);
                  }}
                >
                  Clear
                </Button>
              </DropdownMenuLabel>
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        {tokensList?.tokens.map((item, index) => {
          return (
            <div key={item.id}>
              <DropdownMenuCheckboxItem
                className="flex gap-2 justify-between items-center"
                // Enable for multiple request
                // checked={selectedTokens.includes(item.symbol)}
                checked={item.symbol === selectedToken}
                onCheckedChange={() => {
                  handleOnCheckDisplay(item.symbol ?? "");
                }}
                // Enable for multiple request
                // onSelect={(event) => {
                //   event.preventDefault();
                // }}
              >
                <p className="font-medium">{item.name}</p>
                <p className="opacity-50">({item.symbol})</p>
              </DropdownMenuCheckboxItem>
              {tokensList?.tokens.length - 1 !== index && (
                <DropdownMenuSeparator />
              )}
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
